import React, { useEffect, useRef, useState, useCallback } from "react";
import Hls from "hls.js";
import {
  FiPlay,
  FiPause,
  FiVolume2,
  FiVolumeX,
  FiMaximize,
  FiInfo,
} from "react-icons/fi";
import { CiImageOn } from "react-icons/ci";
import style from "./SubmissionDetail.module.css";

const fmtTime = (secs) => {
  if (!secs || Number.isNaN(secs) || !Number.isFinite(secs)) return "0:00";
  const s = Math.floor(secs % 60);
  const m = Math.floor(secs / 60) % 60;
  const h = Math.floor(secs / 3600);
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};

/**
 * HLS-aware video player with an ALWAYS-VISIBLE custom control bar (play/pause,
 * seek, time, volume/mute, fullscreen). Native controls auto-hide; this bar
 * doesn't. hls.js is used for .m3u8 (native only in Safari); plain URLs go
 * straight to the <video> element.
 */
function SubmissionPlayer({ src, poster }) {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const hlsRef = useRef(null);

  const [failed, setFailed] = useState(false);
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);

  // Attach the source (hls.js or native) whenever src changes.
  useEffect(() => {
    setFailed(false);
    setStarted(false);
    setPlaying(false);
    setCurrent(0);
    setDuration(0);

    const videoEl = videoRef.current;
    if (!videoEl || !src) return;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const isM3u8 = /\.m3u8($|\?)/i.test(src);
    try {
      if (!isM3u8) {
        videoEl.src = src;
      } else if (Hls.isSupported()) {
        // Prefer hls.js over native HLS: recent Chrome reports native HLS
        // support, but its native player doesn't retry a slow/failed CDN
        // segment and stalls silently. hls.js retries and can recover.
        const hls = new Hls({
          fragLoadingMaxRetry: 6,
          manifestLoadingMaxRetry: 4,
          levelLoadingMaxRetry: 4,
        });
        hlsRef.current = hls;
        let recoveries = 0;
        let recoveredAt = 0;
        hls.loadSource(src);
        hls.attachMedia(videoEl);
        hls.on(Hls.Events.FRAG_BUFFERED, () => {
          // Playback has moved on since the last recovery: restore the budget
          // so occasional blips over a long preview don't add up to a failure.
          if (recoveries && videoEl.currentTime > recoveredAt + 10) recoveries = 0;
        });
        hls.on(Hls.Events.ERROR, (_e, data) => {
          if (!data?.fatal) return;
          if (recoveries < 2 && data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            recoveries += 1;
            recoveredAt = videoEl.currentTime;
            hls.startLoad();
          } else if (recoveries < 2 && data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            recoveries += 1;
            recoveredAt = videoEl.currentTime;
            hls.recoverMediaError();
          } else {
            // Giving up: tear hls.js down now (cleanup only runs on src change)
            // and clear the ref so it doesn't mask native onError later.
            hls.destroy();
            if (hlsRef.current === hls) hlsRef.current = null;
            setFailed(true);
          }
        });
      } else if (videoEl.canPlayType("application/vnd.apple.mpegurl")) {
        // iOS Safari: no MSE, so native HLS is the only option.
        videoEl.src = src;
      } else {
        setFailed(true);
      }
    } catch {
      setFailed(true);
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [src]);

  const togglePlay = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    setStarted(true);
    if (el.paused) el.play?.().catch(() => {});
    else el.pause?.();
  }, []);

  const onSeek = (e) => {
    const el = videoRef.current;
    if (!el || !duration) return;
    const t = (Number(e.target.value) / 100) * duration;
    el.currentTime = t;
    setCurrent(t);
  };

  const toggleMute = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  };

  const onVolume = (e) => {
    const el = videoRef.current;
    if (!el) return;
    const v = Number(e.target.value) / 100;
    el.volume = v;
    el.muted = v === 0;
    setVolume(v);
    setMuted(v === 0);
  };

  const goFullscreen = () => {
    const node = wrapRef.current;
    if (!node) return;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else node.requestFullscreen?.();
  };

  if (!src) {
    return (
      <div className={style.playerEmpty}>
        <CiImageOn size={48} />
        <span>No playable stream for this submission yet</span>
      </div>
    );
  }
  if (failed) {
    return (
      <div className={style.playerEmpty}>
        <FiInfo size={40} />
        <span>Preview couldn’t load. The file may still be encoding.</span>
      </div>
    );
  }

  const pct = duration ? (current / duration) * 100 : 0;

  return (
    <div className={style.playerInner} ref={wrapRef}>
      <video
        ref={videoRef}
        className={style.player}
        playsInline
        poster={poster || undefined}
        onClick={togglePlay}
        onPlay={() => {
          setPlaying(true);
          setStarted(true);
        }}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => setCurrent(e.target.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.target.duration || 0)}
        onEnded={() => setPlaying(false)}
        onError={() => {
          // hls.js reports its own errors (and may recover); only native
          // playback should flip to the error state here.
          if (!hlsRef.current) setFailed(true);
        }}
      />

      {/* Big center play button before first play */}
      {!started && (
        <button
          type="button"
          className={style.playOverlay}
          onClick={togglePlay}
          aria-label="Play submission"
        >
          <span className={style.playCircle}>
            <FiPlay size={30} />
          </span>
        </button>
      )}

      {/* Always-visible custom control bar */}
      <div className={style.controlBar}>
        <button
          className={style.ctrlBtn}
          onClick={togglePlay}
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <FiPause size={18} /> : <FiPlay size={18} />}
        </button>

        <span className={style.ctrlTime}>{fmtTime(current)}</span>

        <input
          type="range"
          className={style.seekBar}
          min={0}
          max={100}
          step={0.1}
          value={pct}
          onChange={onSeek}
          style={{
            background: `linear-gradient(to right, #b5883b ${pct}%, #444 ${pct}%)`,
          }}
          aria-label="Seek"
        />

        <span className={style.ctrlTime}>{fmtTime(duration)}</span>

        <button
          className={style.ctrlBtn}
          onClick={toggleMute}
          aria-label={muted ? "Unmute" : "Mute"}
        >
          {muted || volume === 0 ? <FiVolumeX size={18} /> : <FiVolume2 size={18} />}
        </button>

        <input
          type="range"
          className={style.volumeBar}
          min={0}
          max={100}
          step={1}
          value={muted ? 0 : volume * 100}
          onChange={onVolume}
          aria-label="Volume"
        />

        <button
          className={style.ctrlBtn}
          onClick={goFullscreen}
          aria-label="Fullscreen"
        >
          <FiMaximize size={17} />
        </button>
      </div>
    </div>
  );
}

export default SubmissionPlayer;
